import {Payouts} from '@airwallex/components-sdk'

import {
  useCreateAirwallexBeneficiaryMutation,
  useGetBeneficiaryIdQuery,
  useUpdateAirwallexBeneficiaryMutation,
} from '@progyny/generated/graphql'

type SaveResult = {
  success: boolean
  beneficiaryId?: string | null
  errorCode?: string | null
  errors?: Array<{field: string; messages: string[]} | null> | null
} | null

export const useBeneficiarySave = () => {
  const [createBeneficiary] = useCreateAirwallexBeneficiaryMutation()
  const [updateBeneficiary] = useUpdateAirwallexBeneficiaryMutation()

  const {data: beneficiaryData, loading: beneficiaryLoading} =
    useGetBeneficiaryIdQuery()

  const beneficiaryId = beneficiaryData?.airwallexBeneficiaryId ?? null

  // The raw `airwallexBeneficiary` response includes top-level fields such as
  // `id` and `payerEntityType` that are not part of BeneficiaryFormDefaultValues.
  // Passing them to createElement causes the SDK's internal BeneficiaryFormSchema
  // query to fail with BAD_USER_INPUT ("Field id is not defined by type
  // BeneficiaryContactInput"). Explicitly pick only the accepted fields.
  //
  // transfer_methods IS passed through for an existing beneficiary: it is the
  // saved, correct method for the saved country, so the SDK resolves the
  // country-specific bank fields (IBAN, account number, routing) on initial
  // load and the saved values in `beneficiary.bank_details` populate them.
  // Without it those fields are dynamically generated and defaultValues do not
  // apply, leaving the form showing only country + currency.
  const rawBeneficiary = beneficiaryData?.airwallexBeneficiary ?? null
  const existingBeneficiary: Payouts.BeneficiaryFormDefaultValues | null =
    rawBeneficiary
      ? {
          beneficiary: rawBeneficiary.beneficiary,
          ...(rawBeneficiary.nickname && {nickname: rawBeneficiary.nickname}),
          ...(rawBeneficiary.transfer_methods && {
            transfer_methods: rawBeneficiary.transfer_methods,
          }),
        }
      : null

  const saveBeneficiary = async (
    payload: Payouts.BeneficiaryFormValue
  ): Promise<SaveResult> => {
    if (beneficiaryId) {
      const {data} = await updateBeneficiary({
        variables: {beneficiaryId, payload},
      })
      return data?.updateAirwallexBeneficiary ?? null
    }

    const {data} = await createBeneficiary({variables: {payload}})
    return data?.createAirwallexBeneficiary ?? null
  }

  return {
    saveBeneficiary,
    beneficiaryId,
    existingBeneficiary,
    beneficiaryLoading,
  }
}
